import { useMemo, useState } from 'react';
import { buildHardwareReferenceRows } from 'utils/hardwareReference';
import { getAllUniquePartNumbers } from 'utils/partNumberUtils.jsx';
import { useConfigInputs, useBom, useBaselineMetrics, usePricing, useVendorCtx, useExecCtx, useCalcStatus } from 'shared/state/configContexts';
import { useAbility, PERM } from 'shared/access';
import { bomTotals, SEPARATOR, SOLUTION_ROW } from './bomTotals';
import type { BomRow } from 'shared/state/types';

export function useBomSection() {
  const { config } = useConfigInputs();
  const bom = useBom();
  // CONF-212: the REAL metrics — the hardware reference and the active part
  // numbers describe the hardware actually quoted, never an exec's display override.
  const metrics = useBaselineMetrics();
  const { priceBook } = usePricing();
  const { vendor } = useVendorCtx();
  const exec = useExecCtx();
  const { isCalculating } = useCalcStatus();
  const ability = useAbility();

  const [showDiscountColumns, setShowDiscountColumns] = useState(false);

  const canSeeDiscounts = ability.can(PERM.RATES_EDIT) || ability.can(PERM.EXEC_CUSTOM_PRICING);
  const canExec = ability.can(PERM.EXEC_CUSTOM_PRICING);
  const softwareOnly = vendor.businessRules.softwareSubscriptionOnly === true;

  // The separator stays in: the table draws it as the rule between the software
  // and hardware lines, as classic does (App.js:1163). It is not a line item.
  const rows: BomRow[] = useMemo(
    () => (bom ?? []).filter((r) => r && r.item && r.showInUi !== false && r.item !== SOLUTION_ROW),
    [bom],
  );
  const lineCount = rows.filter((r) => r.item !== SEPARATOR).length;
  const totals = useMemo(() => bomTotals(bom), [bom]);
  // Over the WHOLE bom, not the UI-filtered rows — classic's own flag is
  // `bom.some(r => r.isCustomPrice)` (App.js:1187).
  const hasCustomPrice = useMemo(() => (bom ?? []).some((r) => r?.isCustomPrice), [bom]);
  const hardwareReferenceRows = useMemo(
    () => (softwareOnly && metrics ? buildHardwareReferenceRows(config, metrics, vendor.displayName) : []),
    [softwareOnly, config, metrics, vendor.displayName],
  );
  const activePartNumbers = useMemo(
    () => new Set(getAllUniquePartNumbers(config, priceBook, metrics) || []),
    [config, priceBook, metrics],
  );

  return {
    rows, totals, isCalculating, softwareOnly, hardwareReferenceRows, hasCustomPrice,
    solutionLabel: softwareOnly ? 'VDURA Pricing' : 'Solution Price',
    canSeeDiscounts, showDiscountColumns, toggleDiscountColumns: () => setShowDiscountColumns((v) => !v),
    canExec, execOpen: exec.execPanelOpen,
    toggleExec: () => { exec.execPricingTouched.current = true; exec.setExecPanelOpen(!exec.execPanelOpen); },
    execPricing: exec.execPricing, priceBook, config, activePartNumbers,
    summary: `${lineCount} line${lineCount === 1 ? '' : 's'}`,
    isSeparator: (r: BomRow) => r.item === SEPARATOR,
  };
}
